import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Loader2, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { createOrganization, checkSlug, checkOrgName } from '../services/organizationService';
import { useOrgStore } from '../store/orgStore';
import Wordmark from '../components/Brand';

/** 조직명 → 슬러그 자동 생성 */
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[가-힣ㄱ-ㅎㅏ-ㅣ]/g, '') // 한글 제거
    .replace(/[^a-z0-9\s-]/g, '')       // 특수문자 제거
    .trim()
    .replace(/\s+/g, '-')              // 공백 → 하이픈
    .replace(/-+/g, '-')               // 연속 하이픈 정리
    .replace(/^-|-$/g, '');             // 앞뒤 하이픈 제거
}

type NameStatus = 'idle' | 'checking' | 'available' | 'taken';
type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/;

export default function OnboardingCreatePage() {
  const navigate = useNavigate();
  const { setActiveOrgId, fetchOrganizations } = useOrgStore();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [nameStatus, setNameStatus] = useState<NameStatus>('idle');
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [creating, setCreating] = useState(false);

  const nameCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 조직명 변경 시 슬러그 자동 생성 (수동 편집 전까지)
  useEffect(() => {
    if (slugManuallyEdited) return;
    const generated = toSlug(name);
    setSlug(generated);
  }, [name, slugManuallyEdited]);

  // 조직명 변경 시 debounce 중복 체크
  useEffect(() => {
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);

    const trimmed = name.trim();
    if (!trimmed || trimmed.length < 2) {
      setNameStatus('idle');
      return;
    }

    setNameStatus('checking');
    nameCheckTimer.current = setTimeout(async () => {
      try {
        const available = await checkOrgName(trimmed);
        setNameStatus(available ? 'available' : 'taken');
      } catch {
        setNameStatus('idle');
      }
    }, 300);

    return () => {
      if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);
    };
  }, [name]);

  // 슬러그 변경 시 debounce 중복 체크
  useEffect(() => {
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);

    if (!slug || slug.length < 2) {
      setSlugStatus('idle');
      return;
    }

    if (!SLUG_REGEX.test(slug)) {
      setSlugStatus('invalid');
      return;
    }

    setSlugStatus('checking');
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const available = await checkSlug(slug);
        setSlugStatus(available ? 'available' : 'taken');
      } catch {
        setSlugStatus('idle');
      }
    }, 300);

    return () => {
      if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    };
  }, [slug]);

  const handleSlugChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(sanitized);
    setSlugManuallyEdited(true);
  };

  const canSubmit =
    name.trim().length >= 2 &&
    nameStatus === 'available' &&
    slug.length >= 2 &&
    slugStatus === 'available' &&
    !creating;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setCreating(true);
    try {
      const org = await createOrganization({ name: name.trim(), slug });

      // 활성 조직으로 설정 + 조직 목록 새로고침
      setActiveOrgId(org.id);
      await fetchOrganizations();

      toast.success('워크스페이스가 생성되었습니다!');
      navigate('/', { replace: true });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data
          ?.message ??
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        '워크스페이스 생성에 실패했습니다.';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Wordmark className="text-3xl mb-1" />
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Title bar */}
          <div className="px-8 py-5 border-b border-gray-100 flex items-center gap-3">
            <button
              onClick={() => navigate('/onboarding')}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="size-5" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900">워크스페이스 만들기</h2>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-5">
            {/* 조직명 */}
            <div>
              <label htmlFor="org-name" className="block text-sm font-medium text-gray-700 mb-1">
                조직명 <span className="text-red-500">*</span>
              </label>
              <input
                id="org-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="예: 우리 팀"
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none transition-colors
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
              <div className="mt-1 h-4">
                <span className="text-sm flex items-center gap-1">
                  {name.length > 0 && name.trim().length < 2 && (
                    <span className="text-red-500">조직명은 2자 이상이어야 합니다.</span>
                  )}
                  {nameStatus === 'checking' && (
                    <>
                      <Loader2 className="size-3 animate-spin text-gray-400" />
                      <span className="text-gray-400">중복 확인 중...</span>
                    </>
                  )}
                  {nameStatus === 'available' && (
                    <>
                      <Check className="size-3 text-green-600" />
                      <span className="text-green-600">사용 가능한 조직명입니다.</span>
                    </>
                  )}
                  {nameStatus === 'taken' && (
                    <>
                      <X className="size-3 text-red-600" />
                      <span className="text-red-600">이미 사용 중인 조직명입니다.</span>
                    </>
                  )}
                </span>
              </div>
            </div>

            {/* 슬러그 */}
            <div>
              <label htmlFor="org-slug" className="block text-sm font-medium text-gray-700 mb-1">
                슬러그 <span className="text-red-500">*</span>
              </label>
              <input
                id="org-slug"
                type="text"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="your-org"
                maxLength={50}
                className={`w-full px-3 py-2 border rounded-lg text-sm outline-none transition-colors
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${slugStatus === 'taken' || slugStatus === 'invalid' ? 'border-red-300' : 'border-gray-300'}
                  ${slugStatus === 'available' ? 'border-green-300' : ''}`}
              />

              {/* 미리보기 + 상태 */}
              <div className="mt-1.5 flex items-center justify-between">
                <span className="text-sm text-gray-400">
                  maios.io/<span className="text-gray-600">{slug || 'your-slug'}</span>
                </span>

                <span className="text-sm flex items-center gap-1">
                  {slugStatus === 'checking' && (
                    <>
                      <Loader2 className="size-3 animate-spin text-gray-400" />
                      <span className="text-gray-400">확인 중...</span>
                    </>
                  )}
                  {slugStatus === 'available' && (
                    <>
                      <Check className="size-3 text-green-600" />
                      <span className="text-green-600">사용 가능</span>
                    </>
                  )}
                  {slugStatus === 'taken' && (
                    <>
                      <X className="size-3 text-red-600" />
                      <span className="text-red-600">이미 사용 중</span>
                    </>
                  )}
                  {slugStatus === 'invalid' && (
                    <>
                      <X className="size-3 text-red-600" />
                      <span className="text-red-600">소문자, 숫자, 하이픈만 허용</span>
                    </>
                  )}
                </span>
              </div>
            </div>

            {/* 플랜 안내 */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
              <Info className="size-4 text-blue-500 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-700">
                Free 플랜으로 시작합니다 (멤버 5명, 스토리지 2GB)
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-primary-600 text-white py-2.5 px-4 rounded-lg text-sm font-medium
                hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {creating ? '생성 중...' : '워크스페이스 만들기'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
